import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// Blog.jsx — Logbook / dev journal
const Blog = (() => {
  const { Book, Arrow, Star, Diamond, Bolt } = PixelIcons;

  const posts = [
    { k: "b1", featured: true,  cover: "/assets/portfolio/pacific-travel-videojuego.jpg", catColor: "var(--ragross-magenta)" },
    { k: "b2", featured: false, cover: "/assets/portfolio/maefloresta.png",               catColor: "var(--ragross-cyan)"    },
    { k: "b3", featured: false, cover: "/assets/portfolio/daater-landing.jpg",            catColor: "var(--ragross-gold)"    },
    { k: "b4", featured: false, cover: "/assets/portfolio/natamar.jpg",                   catColor: "var(--ragross-green)"   },
  ];

  const blogStyles = {
    section: {
      padding: "112px 0",
      background: "var(--bg-app)",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, marginBottom: 56 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: { font: "var(--type-display-lg)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "#fff", margin: 0 },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", maxWidth: 640, margin: 0 },

    layout: {
      display: "grid",
      gridTemplateColumns: "1.4fr 1fr",
      gap: 18,
    },

    /* Featured */
    feat: {
      position: "relative",
      background: "var(--ragross-purple-deep)",
      border: "2px solid var(--ragross-magenta)",
      boxShadow: "var(--shadow-pixel-cy)",
      borderRadius: "var(--radius-card)",
      overflow: "hidden",
      display: "flex", flexDirection: "column",
      minHeight: 460,
      cursor: "pointer",
      transition: "transform 200ms var(--ease-arcade), box-shadow 200ms",
    },
    featCover: {
      flex: 1,
      position: "relative",
      backgroundSize: "cover", backgroundPosition: "center",
    },
    featBadge: {
      position: "absolute", top: 14, left: 14,
      padding: "6px 10px",
      background: "var(--ragross-gold)",
      color: "var(--fg-on-gold)",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9,
      letterSpacing: "0.16em",
      boxShadow: "4px 4px 0 var(--ragross-purple-night)",
    },
    featScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)", opacity: 0.5, pointerEvents: "none",
    },
    featGradient: {
      position: "absolute", left: 0, right: 0, bottom: 0,
      height: "60%",
      background: "linear-gradient(180deg, transparent 0%, rgba(11,0,20,0.95) 80%)",
    },
    featMeta: {
      position: "absolute", left: 24, right: 24, bottom: 24,
      display: "flex", flexDirection: "column", gap: 14,
      zIndex: 2,
    },
    featCat: {
      display: "inline-block",
      padding: "5px 9px",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9, letterSpacing: "0.16em",
      background: "var(--ragross-magenta)",
      color: "#fff",
      width: "fit-content",
    },
    featTitle: { font: "var(--type-display-md)", color: "#fff", margin: 0, lineHeight: 1.1 },
    featMetaRow: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9, letterSpacing: "0.12em",
      color: "var(--ragross-cyan)",
    },

    /* Sidebar list */
    list: { display: "flex", flexDirection: "column", gap: 14 },
    postCard: {
      display: "grid",
      gridTemplateColumns: "110px 1fr",
      gap: 14,
      background: "var(--bg-surface)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      overflow: "hidden",
      cursor: "pointer",
      transition: "transform 200ms var(--ease-arcade), border-color 200ms, box-shadow 200ms",
    },
    postCover: {
      backgroundSize: "cover", backgroundPosition: "center",
      position: "relative", minHeight: 110,
    },
    postCoverScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)", opacity: 0.4, pointerEvents: "none",
    },
    postBody: { padding: "14px 14px 14px 0", display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" },
    postCat: (color) => ({
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.16em",
      color,
    }),
    postTitle: { font: "var(--type-h3)", color: "#fff", margin: 0, lineHeight: 1.25, fontSize: 16 },
    postMeta: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.10em",
      color: "var(--fg-3)",
    },
  };

  function PostCard({ p }) {
    const t = useT();
    const [h, setH] = React.useState(false);
    return (
      <article
        style={{
          ...blogStyles.postCard,
          borderColor: h ? p.catColor : blogStyles.postCard.borderColor,
          transform: h ? "translate(-2px,-2px)" : "none",
          boxShadow: h ? `4px 4px 0 ${p.catColor}` : "none",
        }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
      >
        <div style={{ ...blogStyles.postCover, backgroundImage: `url(${p.cover})` }}>
          <div style={blogStyles.postCoverScan}></div>
        </div>
        <div style={blogStyles.postBody}>
          <span style={blogStyles.postCat(p.catColor)}>▸ {t(`blog.${p.k}.cat`)}</span>
          <h3 style={blogStyles.postTitle}>{t(`blog.${p.k}.title`)}</h3>
          <span style={blogStyles.postMeta}>{t(`blog.${p.k}.meta`)}</span>
        </div>
      </article>
    );
  }

  function Blog() {
    const t = useT();
    const featured = posts.find(p => p.featured);
    const rest = posts.filter(p => !p.featured);
    const [h, setH] = React.useState(false);

    return (
      <section id="blog" style={blogStyles.section} data-screen-label="08 Blog">
        <div style={blogStyles.container}>
          <div style={blogStyles.head}>
            <div style={blogStyles.tag}>
              <span>{t("blog.tag")}</span>
              <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
            </div>
            <h2 style={blogStyles.title} className="section-title">
              {t("blog.title1")} <span style={{ color: "var(--ragross-cyan)" }}>{t("blog.title2")}</span>
            </h2>
            <p style={blogStyles.lead}>{t("blog.lead")}</p>
          </div>

          <div style={blogStyles.layout} className="responsive-grid-2">
            <article
              style={{
                ...blogStyles.feat,
                transform: h ? "translate(-3px,-3px)" : "none",
                boxShadow: h ? "8px 8px 0 var(--ragross-cyan), 0 0 40px rgba(253,20,145,0.3)" : blogStyles.feat.boxShadow,
              }}
              onMouseEnter={() => setH(true)}
              onMouseLeave={() => setH(false)}
            >
              <div style={{ ...blogStyles.featCover, backgroundImage: `url(${featured.cover})` }}>
                <span style={blogStyles.featBadge}>▸ {t("blog.featured")}</span>
                <div style={blogStyles.featScan}></div>
                <div style={blogStyles.featGradient}></div>
              </div>
              <div style={blogStyles.featMeta}>
                <span style={blogStyles.featCat}>{t(`blog.${featured.k}.cat`)}</span>
                <h3 style={blogStyles.featTitle}>{t(`blog.${featured.k}.title`)}</h3>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={blogStyles.featMetaRow}>{t(`blog.${featured.k}.meta`)}</span>
                  <span className="btn btn-secondary" style={{ padding: "10px 14px", fontSize: 9 }}>
                    <Book size={11}/>
                    <span>{t("blog.read")}</span>
                    <Arrow size={10} color="var(--ragross-cyan)"/>
                  </span>
                </div>
              </div>
            </article>

            <div style={blogStyles.list}>
              {rest.map((p) => <PostCard key={p.k} p={p}/>)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return Blog;
})();

export default Blog;
